/**
 * Enhanced HTTP Service with Automatic Authentication
 * 
 * This service wraps fetch requests and automatically adds authentication headers
 * using the secure storage service. It provides a consistent API for all HTTP calls.
 */

import authStorageService from '../auth/auth-storage.service.js';
import router from '@/router';

class HttpService {
    constructor() {
        this.baseURL = '';
        this.defaultHeaders = {
            'Content-Type': 'application/json'
        };
    }

    /**
     * Set base URL for all requests
     * @param {string} url - Base URL
     */
    setBaseURL(url) {
        this.baseURL = url;
    }

    /**
     * Get authentication headers automatically
     * @returns {Object} Headers object with Authorization if available
     */
    getAuthHeaders() {
        const token = authStorageService.getAccessToken();
        const headers = { ...this.defaultHeaders };
        
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        
        return headers;
    }

    /**
     * Make an authenticated HTTP request
     * @param {string} url - Request URL
     * @param {Object} options - Fetch options
     * @returns {Promise} Fetch promise
     */
    async request(url, options = {}) {
        const fullURL = this.baseURL + url;
        
        // Merge authentication headers with any provided headers
        const headers = {
            ...this.getAuthHeaders(),
            ...(options.headers || {})
        };

        // Support request timeouts via AbortController. Default timeout = 120s
        const controller = new AbortController();
        const timeoutMs = typeof options.timeout === 'number' ? options.timeout : 120000;

        const requestOptions = {
            ...options,
            headers,
            signal: controller.signal
        };

        // Start timeout countdown
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        try {
            const response = await fetch(fullURL, requestOptions);
            
            // Handle 401 unauthorized responses
            if (response.status === 401 || response.status === 403) {
                console.warn('Authentication failed, clearing stored data');
                authStorageService.clearAuthData();
                
                // Optionally redirect to login
                if (typeof window !== 'undefined' && router.currentRoute.path !== '/login') {
                    router.push({ name: 'login' }).catch((navError) => {
                        console.warn('Router redirect to login failed:', navError);
                        window.location.replace(`${window.location.origin}/#/login`);
                    });
                }
            }

            return response;
        } catch (error) {
            // Differentiate abort/timeout errors for clearer messaging
            if (error && error.name === 'AbortError') {
                const err = new Error(`Request timed out after ${timeoutMs} ms`);
                err.name = 'TimeoutError';
                console.error('HTTP request timed out:', err);
                throw err;
            }

            console.error('HTTP request failed:', error);
            throw error;
        }
        finally {
            // Clear the timeout to avoid leaks
            clearTimeout(timeoutId);
        }
    }

    /**
     * GET request with automatic authentication
     * @param {string} url - Request URL
     * @param {Object} options - Additional options
     * @returns {Promise} Response promise
     */
    async get(url, options = {}) {
        return this.request(url, {
            method: 'GET',
            ...options
        });
    }

    /**
     * POST request with automatic authentication
     * @param {string} url - Request URL
     * @param {any} data - Request body data
     * @param {Object} options - Additional options
     * @returns {Promise} Response promise
     */
    async post(url, data, options = {}) {
        return this.request(url, {
            method: 'POST',
            body: JSON.stringify(data),
            ...options
        });
    }

    /**
     * PUT request with automatic authentication
     * @param {string} url - Request URL
     * @param {any} data - Request body data
     * @param {Object} options - Additional options
     * @returns {Promise} Response promise
     */
    async put(url, data, options = {}) {
        return this.request(url, {
            method: 'PUT',
            body: JSON.stringify(data),
            ...options
        });
    }

    /**
     * DELETE request with automatic authentication
     * @param {string} url - Request URL
     * @param {Object} options - Additional options
     * @returns {Promise} Response promise
     */
    async delete(url, options = {}) {
        return this.request(url, {
            method: 'DELETE',
            ...options
        });
    }

    /**
     * PATCH request with automatic authentication
     * @param {string} url - Request URL
     * @param {any} data - Request body data
     * @param {Object} options - Additional options
     * @returns {Promise} Response promise
     */
    async patch(url, data, options = {}) {
        return this.request(url, {
            method: 'PATCH',
            body: JSON.stringify(data),
            ...options
        });
    }

    /**
     * Make a request and parse JSON response
     * @param {string} url - Request URL
     * @param {Object} options - Fetch options
     * @returns {Promise} Parsed JSON response
     */
    async getJSON(url, options = {}) {
        const response = await this.get(url, options);
        return response.json();
    }

    /**
     * Make a POST request and parse JSON response
     * @param {string} url - Request URL
     * @param {any} data - Request body data
     * @param {Object} options - Additional options
     * @returns {Promise} Parsed JSON response
     */
    async postJSON(url, data, options = {}) {
        const response = await this.post(url, data, options);
        return response.json();
    }

    /**
     * Upload files with automatic authentication
     * @param {string} url - Upload URL
     * @param {FormData} formData - Form data with files
     * @param {Object} options - Additional options
     * @returns {Promise} Response promise
     */
    async uploadFiles(url, formData, options = {}) {
        // Don't set Content-Type for file uploads, let browser set it
        const headers = this.getAuthHeaders();
        delete headers['Content-Type'];

        return this.request(url, {
            method: 'POST',
            body: formData,
            headers,
            ...options
        });
    }
}

// Create and export singleton instance
const httpService = new HttpService();

export { HttpService };
export default httpService;
